import React, { useMemo } from 'react';

import { Button, Checkbox, Divider, Space, Typography } from 'antd';

import { useLifecycleStatuses } from '../api/lifecyle/getLifecycle';

type TDropdownProps = {
  setSelectedKeys: (keys: React.Key[]) => void;
  selectedKeys: React.Key[];
  confirm: (param?: { closeDropdown?: boolean }) => void;
  clearFilters?: () => void;
};

export const LifecycleFilter = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}: TDropdownProps) => {
  const { data } = useLifecycleStatuses();

  const keys = useMemo(
    () => (selectedKeys ? (selectedKeys as React.Key[]).map(String) : []),
    [selectedKeys]
  );

  const childrenByStatus = useMemo(() => {
    const map: Record<string, string[]> = {};
    (data?.statuses || []).forEach(({ status, sub_statuses }) => {
      map[status] = (sub_statuses || []).map((s) => `${status}-${s}`);
    });

    return map;
  }, [data]);

  const onToggleParent = (status: string, checked: boolean) => {
    const parentKey = status;
    const childKeys = childrenByStatus[status] || [];

    if (checked) {
      const added = new Set<string>([...keys, parentKey]);
      setSelectedKeys(Array.from(added));

      return;
    }

    const filtered = keys.filter(
      (k) => k !== parentKey && !childKeys.includes(k as string)
    );
    setSelectedKeys(filtered);
  };

  const onToggleChild = (
    status: string,
    subStatus: string,
    checked: boolean
  ) => {
    const childKey = `${status}-${subStatus}`;
    const parentKey = status;
    if (checked) {
      const added = new Set<string>([...keys, parentKey, childKey]);
      setSelectedKeys(Array.from(added));

      return;
    }

    setSelectedKeys(keys.filter((k) => k !== childKey));
  };

  const isParentChecked = (status: string) => keys.includes(status);

  const selectedChildrenCount = (status: string) =>
    (childrenByStatus[status] || []).filter((k) => keys.includes(k)).length;

  const totalChildrenCount = (status: string) =>
    (childrenByStatus[status] || []).length;

  const isChildChecked = (status: string, sub: string) =>
    keys.includes(`${status}-${sub}`);

  return (
    <Space style={{ padding: 12, width: 320 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text strong>Filtrer par lifecycle</Typography.Text>
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ maxHeight: 260, overflow: 'auto', paddingRight: 6 }}>
          {(data?.statuses || []).map(({ status, sub_statuses }) => (
            <div key={status} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={isParentChecked(status)}
                indeterminate={
                  selectedChildrenCount(status) > 0 &&
                  selectedChildrenCount(status) < totalChildrenCount(status)
                }
                onChange={(e) => onToggleParent(status, e.target.checked)}
              >
                {status}
              </Checkbox>
              {sub_statuses?.length ? (
                <div style={{ paddingLeft: 16, marginTop: 4 }}>
                  {sub_statuses.map((sub) => (
                    <div key={`${status}-${sub}`}>
                      <Checkbox
                        checked={isChildChecked(status, sub)}
                        onChange={(e) =>
                          onToggleChild(status, sub, e.target.checked)
                        }
                      >
                        {sub}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              if (clearFilters) clearFilters();
              setSelectedKeys([]);
              confirm({ closeDropdown: false });
            }}
            disabled={keys.length === 0}
          >
            Reset
          </Button>
          <Space>
            <Button onClick={() => confirm({ closeDropdown: false })}>
              Apply
            </Button>
            <Button type="primary" onClick={() => confirm()}>
              OK
            </Button>
          </Space>
        </Space>
      </Space>
    </Space>
  );
};
